// Third Party
import React from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import moment from 'moment';

// Components
import ComparisonBaseCell from './ComparisonBaseCell';
import { TextCell, NotApplicableCell, HoldingCell } from '../../../components/cells';
import { Comment, Circle } from '../../../components/markup-tools';
import { Dot } from '../../../components/buttons';

// Types
import ComparisonField from '@/types/comparison-field';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';
import BasicUserInfos from '@/types/basic-user-infos';
import RiskPeriods from '@/types/risk-periods';
import Comparison from '@/types/comparison';
import MarkupComment from '@/types/markup-comment';
import Markup from '@/types/markup';
import Locale from '@/types/locale';

// Utils
import { MARKUP_TOOLS, DOT_COLORS, getUniqueFieldId } from '@/utils/markup-tools';
import { COMPARISON_CATEGORIES, COMPARISON_ITEM_TYPES, TIME_PERIODS, UNIT_BASE } from '@/utils/constants';
import { getCountryName, formatLocalizedString, formatMessageWithLocale, LOCALE } from '@/utils/i18n';
import { comparisonIsReadOnly, isBenchmark } from '@/utils/comparison';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { getMaxCommonInceptionDate } from '@/utils/common-inception';
import { isRiskCategory, isCalendarYearCategory } from './utils/comparison-category';
import NoDataText from '@/types/no-data-text';

type InterfaceProps = {
  className?: string;
  field: ComparisonField;
  category: ComparisonCategory;
  comparisonItem: ComparisonItem;
  viewMarkup: boolean;
  previewMarkup?: boolean;
  markupTool?: string;
  markupMode?: boolean;
  onClick?: () => void;
  setMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, comment: string) => void;
  postMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, comment: string) => void;
  getNewDotNumber: () => number;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  user?: User | null;
  userInfos: Partial<BasicUserInfos>;
  riskPeriod: RiskPeriods;
  onShowCorrelation: (comparisonItem: ComparisonItem) => void;
  showBaseCurrencyValues: boolean;
  readOnly?: boolean;
  locale?: Locale;
  useLongHistory?: boolean;
};

type StateProps = {
  comparison: Comparison;
};

type Props = InterfaceProps & StateProps;

type State = {
  commentOpen: boolean;
  isAddingNewComment: boolean;
  circleWidth?: number;
  circleHeight?: number;
};

const defaultDotColor = DOT_COLORS.GREEN;

class ComparisonValueCell extends React.Component<Props, State> {
  readonly state: State = {
    commentOpen: false,
    isAddingNewComment: false,
  };

  componentDidMount() {
    const { comparisonItem: item, category, field } = this.props;

    if (this.hasCircle(item, category, field)) {
      this.calculateCircleDimensions(item, category, field);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { previewMarkup, comparisonItem: item, category, field } = this.props;

    const circleChanged = this.hasCircle(prevProps.comparisonItem, category, field) !== this.hasCircle(item, category, field);

    if (prevProps.previewMarkup !== previewMarkup || circleChanged) {
      this.calculateCircleDimensions(item, category, field);
    }
  }

  onClickOutside = () => {
    this.setState({ isAddingNewComment: false, commentOpen: false });
  };

  getValue = (field: ComparisonField, item: ComparisonItem, riskPeriod?: RiskPeriods) => {
    if (field.accessor && field.multiValue && riskPeriod) {
      return this.getCellValue(item, field.key, field.accessor, riskPeriod);
    }
    if (field.accessor) {
      return this.getCellValue(item, field.key, field.accessor);
    }
    if (field.multiValue && riskPeriod) {
      return this.getCellValue(item, field.key, undefined, riskPeriod);
    }
    return this.getCellValue(item, field.key);
  };

  getCellValue = (
    comparisonItem: ComparisonItem,
    key: string,
    accessor?: (item: ComparisonItem, riskPeriod?: RiskPeriods) => any,
    riskPeriod?: RiskPeriods,
  ): any => {
    const { showBaseCurrencyValues } = this.props;

    if (accessor) {
      return accessor(comparisonItem, riskPeriod);
    }

    // Keys use '_' as separators and we need to replace them with '.' when accessing a field
    let formattedKey = key.replace(/_/g, '.');

    if (showBaseCurrencyValues) {
      formattedKey = formattedKey.replace('performance', 'performanceBase');
    }

    return riskPeriod ? get(comparisonItem, `${formattedKey}.${riskPeriod}`, null) : get(comparisonItem, formattedKey, null);
  };

  getValueDisclaimer = (field: ComparisonField, item: ComparisonItem) => {
    const { locale, useLongHistory } = this.props;
    const isSinceInception = field.key === `performance_percentGrowth_${TIME_PERIODS.INC}`;

    const valueDisclaimer = field.getValueDisclaimer && field.getValueDisclaimer(item);

    if (isSinceInception && (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP)) {
      const localeDateFormat = formatMessageWithLocale('Common.DateFormats.Short', locale || LOCALE.EN_CA);
      const sinceInceptionDate = getMaxCommonInceptionDate([item], useLongHistory); // Max common inception of a single item is the since inception
      const message = formatMessageWithLocale('Comparison.Table.SinceInceptionValueTooltip', locale, {
        date: sinceInceptionDate ? moment(sinceInceptionDate).utc().format(localeDateFormat) : '',
      });

      if (valueDisclaimer == null) {
        return {
          title: formatMessageWithLocale('Comparison.Table.Performance.INC', locale),
          message,
        };
      }
      valueDisclaimer.message = `${message} ${valueDisclaimer.message}`;
    }

    return valueDisclaimer;
  };

  getDisclaimer = (field: ComparisonField, item: ComparisonItem) => {
    const fieldDisclaimers = field.disclaimers && field.disclaimers[item.type];
    const { disclaimers } = item;

    if (!fieldDisclaimers || !disclaimers) {
      return null;
    }

    return fieldDisclaimers.find(key => !!disclaimers[key]);
  };

  openComment = (event: React.MouseEvent) => {
    this.setState({ commentOpen: true });

    event.stopPropagation();
  };

  getDotNumber = (comment?: MarkupComment) => {
    if (comment && comment.dotNumber) {
      return comment.dotNumber;
    }

    return this.props.getNewDotNumber();
  };

  showCommentDot = (markupComment?: MarkupComment) => {
    const { viewMarkup, previewMarkup, user, markupMode } = this.props;
    const { isAddingNewComment } = this.state;

    const hasPostedComment = !!markupComment && markupComment.isPosted;

    if (userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)) {
      return (isAddingNewComment || hasPostedComment) && !previewMarkup && markupMode;
    }

    return hasPostedComment && !viewMarkup;
  };

  showComment = (markupComment?: MarkupComment) => {
    const { viewMarkup, previewMarkup, user, markupMode } = this.props;
    const { isAddingNewComment, commentOpen } = this.state;

    const hasPostedComment = !!markupComment && markupComment.isPosted;

    if (userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)) {
      return markupMode && (isAddingNewComment || (hasPostedComment && commentOpen && !previewMarkup));
    }

    return hasPostedComment && commentOpen && !viewMarkup;
  };

  renderComment = (markupComment?: MarkupComment) => {
    const { comparisonItem: item, category, field, clearMarkup, viewMarkup, userInfos, readOnly } = this.props;

    const dotNumber = this.getDotNumber(markupComment);

    const postComment = (comment: string) => {
      this.setState({ isAddingNewComment: false, commentOpen: true });
      this.props.postMarkupComment(item, category, field, dotNumber, comment);
    };

    return (
      <>
        {this.showCommentDot(markupComment) && <Dot className={`dot-${dotNumber}`} number={dotNumber} onClick={this.openComment} color={defaultDotColor} />}

        {this.showComment(markupComment) && (
          <Comment
            dotNumber={dotNumber}
            value={markupComment ? markupComment.value : undefined}
            isPosted={markupComment ? markupComment.isPosted : undefined}
            datePosted={markupComment ? markupComment.timestamp : undefined}
            onClickOutside={this.onClickOutside}
            onCommentChange={this.onMarkupCommentChange}
            postComment={postComment}
            deleteComment={() => clearMarkup(item, category.key, field.key, MARKUP_TOOLS.COMMENT)}
            actionsDisabled={!viewMarkup || readOnly}
            userInfos={userInfos}
          />
        )}
      </>
    );
  };

  onMarkupCommentChange = (comment: string) => {
    const { comparisonItem, category, field } = this.props;
    this.props.setMarkupComment(comparisonItem, category, field, comment);
  };

  addNewComment = () => {
    this.setState({ isAddingNewComment: true });
  };

  renderChild = () => {
    const { field, comparisonItem, category, onShowCorrelation, onClick, markupTool, riskPeriod, previewMarkup, comparison, locale } = this.props;

    let value = this.getValue(field, comparisonItem, riskPeriod);
    const valueDisclaimer = this.getValueDisclaimer(field, comparisonItem);

    if (category.key === COMPARISON_CATEGORIES.INDUSTRY_HOLDINGS && value) {
      value.category = COMPARISON_CATEGORIES.INDUSTRY_HOLDINGS;
    } else if (category.key === COMPARISON_CATEGORIES.COUNTRY_ALLOCATIONS && value) {
      value.category = COMPARISON_CATEGORIES.COUNTRY_ALLOCATIONS;
    } else if (category.key === COMPARISON_CATEGORIES.KEY_METRICS && value) {
      switch (field.key) {
        case 'domicile':
          value = getCountryName(value);
          break;
        case 'classification':
          value = formatLocalizedString(value);
          break;
      }
    }

    const readOnly = comparisonIsReadOnly(comparison);
    const handleClick = markupTool === MARKUP_TOOLS.COMMENT ? this.addNewComment : onClick;
    const disableClick = previewMarkup || readOnly;

    // HoldingCell internally manages showing a Dash OR NotApplicable with Tooltip if disclaimer exists
    const showHoldingCell =
      userHasPermissionEnabled(this.props.user, PERMISSIONS.COMPARISONS_TOP_HOLDINGS_DYNAMIC_COUNT) && category.key === COMPARISON_CATEGORIES.TOP_HOLDINGS;

    if ((field.itemTypes && !field.itemTypes.includes(comparisonItem.type)) || (category.itemTypes && !category.itemTypes.includes(comparisonItem.type))) {
      return showHoldingCell ? (
        <HoldingCell value={{ name: '', weight: null }} />
      ) : (
        <NotApplicableCell
          hideTooltip={isBenchmark(comparisonItem)}
          onClick={!disableClick ? handleClick : undefined}
          id={getUniqueFieldId(comparisonItem, category, field)}
        />
      );
    }

    return React.createElement(field.cell || TextCell, {
      value,
      className: `${field.className || ''} comparison-cell`,
      checkboxOptions: field.checkboxOptions,
      item: comparisonItem,
      disclaimer: this.getDisclaimer(field, comparisonItem),
      activeBenchmark: field.getActiveBenchmark && field.getActiveBenchmark(),
      valueDisclaimer,
      linkDisabled: !previewMarkup ? !!markupTool : false,
      id: getUniqueFieldId(comparisonItem, category, field),
      onClick: !disableClick ? handleClick : undefined,
      riskPeriod,
      onShowCorrelation,
      disabled: field.disabled,
      comparison,
    });
  };

  getMarkupClasses = (categoryKey: string, fieldKey: string, markup?: Markup) => {
    const { markupMode, viewMarkup, user } = this.props;
    const fieldMarkup = markup && markup[categoryKey] && markup[categoryKey][fieldKey] && markup[categoryKey][fieldKey];

    const square = fieldMarkup && fieldMarkup.shape === MARKUP_TOOLS.SQUARE;
    const highlight = fieldMarkup && fieldMarkup.highlight;

    const classes = classNames({ highlight, square });

    if (userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)) {
      return markupMode && classes;
    }

    return viewMarkup && classes;
  };

  showCommentLines = (markupComment?: MarkupComment) => {
    const { viewMarkup, user, markupMode, previewMarkup } = this.props;
    const hasPostedComment = !!markupComment && markupComment.isPosted;

    if (userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)) {
      return markupMode && hasPostedComment && previewMarkup;
    }

    return hasPostedComment && viewMarkup;
  };

  calculateCircleDimensions = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => {
    const id = getUniqueFieldId(item, category, field);
    const markupContainer = document.querySelector(`#cell-${id} .markup-container`) as HTMLElement;
    const cell = document.getElementById(`cell-${id}`);

    if (!cell) {
      return;
    }

    const circleWidth = markupContainer.offsetWidth + 4 * UNIT_BASE;
    const circleHeight = cell.offsetHeight + UNIT_BASE;

    this.setState({ circleWidth, circleHeight });
  };

  renderCircle = () => {
    const { viewMarkup, user, markupMode } = this.props;
    const { circleWidth, circleHeight } = this.state;

    if (!circleWidth || !circleHeight) {
      return null;
    }

    const el = <Circle width={circleWidth} height={circleHeight} />;

    if (userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP)) {
      return markupMode ? el : null;
    }

    return viewMarkup ? el : null;
  };

  getComment = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) =>
    item.markup && item.markup[category.key] && item.markup[category.key][field.key] && item.markup[category.key][field.key].comment;

  hasCircle = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) =>
    item.markup && item.markup[category.key] && item.markup[category.key][field.key] && item.markup[category.key][field.key].shape === MARKUP_TOOLS.CIRCLE;

  generateAriaLabelledBy = (): string => {
    const { category, field, comparisonItem: item } = this.props;
    const categoryKey = category.key;

    const ids = [
      `row-${item._id}`, // Main row header (item name)
      `field-header-${field.key}`, // Field header (already includes category name)
    ];

    // Add date-range ID based on category type
    if (isRiskCategory(categoryKey)) {
      ids.push(`date-range-risk-${categoryKey}`);
    } else if (isCalendarYearCategory(categoryKey)) {
      ids.push(`date-range-calendar-${categoryKey}`);
    }

    return ids.join(' ');
  };

  render() {
    const { comparisonItem: item, category, field, markupTool, className, previewMarkup, comparison } = this.props;

    const readOnly = comparisonIsReadOnly(comparison);
    const markupComment = this.getComment(item, category, field);

    const hasCircle = this.hasCircle(item, category, field);

    const classes = classNames('comparison-table-value-cell', className, field.key, this.getMarkupClasses(category.key, field.key, item.markup), {
      'markup-tool': !!markupTool && !previewMarkup && !readOnly,
      'show-comment-lines': this.showCommentLines(markupComment),
      'visually-hidden': isBenchmark(item) && category.key === COMPARISON_CATEGORIES.KEY_METRICS,
    });

    return (
      <ComparisonBaseCell
        {...this.props}
        className={classes}
        role="cell"
        ariaLabelledBy={this.generateAriaLabelledBy()}
        id={`cell-${category.key}-${field.key}-${item._id}`}
      >
        {this.renderChild()}
        {this.renderComment(markupComment)}
        {hasCircle && this.renderCircle()}
      </ComparisonBaseCell>
    );
  }
}

export default ComparisonValueCell;
